import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { getSession, logout, SIGNED_OUT_EVENT, type SessionInfo } from './api'
import { SessionContext, type SessionState } from './useSession'

/**
 * One view of who is signed in, shared by the header and every page, so a
 * sign-in or sign-out anywhere shows everywhere at once. Checked with the
 * server on mount.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'checking' })

  useEffect(() => {
    let cancelled = false
    getSession()
      .then((session) => {
        if (!cancelled) setState(session ? { status: 'signed-in', session } : { status: 'signed-out' })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'signed-out', error: 'Event access is unavailable right now.' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Any request that finds the session gone signs every view out at once.
  useEffect(() => {
    const ended = () => setState({ status: 'signed-out', error: 'Your session ended. Sign in again.' })
    window.addEventListener(SIGNED_OUT_EVENT, ended)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, ended)
  }, [])

  const signedIn = useCallback((session: SessionInfo) => setState({ status: 'signed-in', session }), [])

  const signOut = useCallback(async () => {
    await logout().catch(() => undefined)
    setState({ status: 'signed-out' })
  }, [])

  const value = useMemo(() => ({ state, signedIn, signOut }), [state, signedIn, signOut])
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

